import { useState, useEffect, useCallback, useMemo } from "react";
import React from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowsClockwise, FileXls, Printer, MagnifyingGlass, FunnelSimple, SlidersHorizontal, Check, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const f2 = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? "0.00" : n.toFixed(2);
};

const SORT_OPTIONS = [
  { value: "balance_value_desc", label: "Highest Balance Value" },
  { value: "dispatch_value_desc", label: "Highest Dispatch Value" },
  { value: "machining_value_desc", label: "Highest Machining Value" },
  { value: "lowest_completion", label: "Lowest Completion" },
  { value: "part_no_asc", label: "Part No (A→Z)" },
];

const PAGE_SIZE = 25;

// LocalStorage key for persisted filter prefs (date_from / date_to /
// critical_only). Stored client-side so each user/device retains their last
// chosen review window across sessions until they explicitly clear it.
const DR_PREFS_KEY = "daily_review_prefs_v1";

function DailyReviewSheet() {
  const [data, setData] = useState({ rows: [], totals: {}, filters: { customers: [] }, count: 0 });
  const [loading, setLoading] = useState(false);
  // Hydrate persisted prefs once at mount. localStorage stores only the
  // sticky bits: date window + critical-only toggle. Customer/part filters
  // stay session-local because they're usually one-off lookups.
  const _saved = (() => {
    try { return JSON.parse(localStorage.getItem(DR_PREFS_KEY) || "{}"); }
    catch { return {}; }
  })();
  const [filters, setFilters] = useState({
    customer: "",
    part_no: "",
    process: "",
    date_from: _saved.date_from || "",
    date_to: _saved.date_to || "",
    sort: "balance_value_desc",
  });
  const [criticalOnly, setCriticalOnly] = useState(!!_saved.critical_only);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [processList, setProcessList] = useState([]);
  // Dynamic process columns come from the backend payload (`process_columns`)
  // which is built from the tenant's Customization sequence. This guarantees
  // the sheet ALWAYS matches what the user configured — no hardcoded
  // Painting / Silver / FG / U/I / O/S columns leaking through.
  const processCols = data.process_columns || [];
  // View mode: "rollup" (default — 5-col: Schedule / Dispatch / RFD / S/F / Machining)
  //         | "expanded" (per-process columns as before).
  const [viewMode, setViewMode] = useState(_saved.view_mode || "rollup");
  const [customColumns, setCustomColumns] = useState([]); // [{code, name, value_kind}]
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [colDraft, setColDraft] = useState({ codes: [], value_kind_by_code: {}, hidden_processes: [] });

  const buildQuery = useCallback((f, s, critOnly) => {
    const params = new URLSearchParams();
    if (f.customer) params.set("customer", f.customer);
    if (f.part_no) params.set("part_no", f.part_no);
    if (f.process) params.set("process", f.process);
    if (f.date_from) params.set("date_from", f.date_from);
    if (f.date_to) params.set("date_to", f.date_to);
    if (f.sort) params.set("sort", f.sort);
    if (s) params.set("search", s);
    if (critOnly) params.set("critical_only", "true");
    return params.toString();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const qs = buildQuery(filters, search, criticalOnly);
      const r = await axios.get(`${API}/daily-review${qs ? `?${qs}` : ""}`, { skipPeriod: true });
      setData(r.data || { rows: [], totals: {}, filters: { customers: [] }, count: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load Daily Review");
    } finally {
      setLoading(false);
    }
  }, [filters, search, criticalOnly, buildQuery]);

  // Persist date window + critical_only on every change. Customer/part stay
  // session-local — they're not usually meant to survive a reload.
  useEffect(() => {
    try {
      localStorage.setItem(DR_PREFS_KEY, JSON.stringify({
        date_from: filters.date_from || "",
        date_to: filters.date_to || "",
        critical_only: criticalOnly,
        view_mode: viewMode,
      }));
    } catch { /* ignore quota errors */ }
  }, [filters.date_from, filters.date_to, criticalOnly, viewMode]);

  // Load processes + saved column config
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/processes`, { skipPeriod: true });
        setProcessList(r.data || []);
      } catch { /* non-fatal */ }
      try {
        const r = await axios.get(`${API}/settings/report-columns/daily_review`, { skipPeriod: true });
        const codes = r.data?.columns || [];
        const vkbc = r.data?.value_kind_by_code || {};
        const hidden = r.data?.hidden_processes || [];
        setColDraft({ codes, value_kind_by_code: vkbc, hidden_processes: hidden });
      } catch { /* non-fatal */ }
    })();
  }, []);

  // Keep customColumns synced from API response so the table header renders dynamic columns
  useEffect(() => {
    setCustomColumns(data.custom_columns || []);
  }, [data.custom_columns]);

  const saveColumnConfig = async () => {
    try {
      await axios.put(`${API}/settings/report-columns/daily_review`, {
        columns: colDraft.codes,
        value_kind_by_code: colDraft.value_kind_by_code,
        hidden_processes: colDraft.hidden_processes,
      }, { skipPeriod: true });
      toast.success("Columns saved");
      setColPickerOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save columns");
    }
  };

  const toggleHideProcess = (code) => {
    setColDraft(d => {
      const set = new Set(d.hidden_processes || []);
      if (set.has(code)) set.delete(code); else set.add(code);
      return { ...d, hidden_processes: Array.from(set) };
    });
  };

  const toggleCol = (code) => {
    setColDraft(d => {
      const has = d.codes.includes(code);
      const codes = has ? d.codes.filter(c => c !== code) : [...d.codes, code];
      const vkbc = { ...d.value_kind_by_code };
      if (!has && !vkbc[code]) vkbc[code] = "produced";
      return { codes, value_kind_by_code: vkbc };
    });
  };

  const setColKind = (code, kind) => {
    setColDraft(d => ({ ...d, value_kind_by_code: { ...d.value_kind_by_code, [code]: kind } }));
  };

  // Initial + auto-refresh every 30s (live data rule)
  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 30_000);
    return () => clearInterval(id);
  }, [fetchData]);

  // Reset page when filters/search change
  useEffect(() => { setPage(1); }, [filters, search]);

  const totalPages = Math.max(1, Math.ceil(data.rows.length / PAGE_SIZE));
  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return data.rows.slice(start, start + PAGE_SIZE);
  }, [data.rows, page]);

  const exportExcel = async () => {
    try {
      const qs = buildQuery(filters, search, criticalOnly);
      const res = await axios.get(`${API}/daily-review/export${qs ? `?${qs}` : ""}`, {
        responseType: "blob", skipPeriod: true,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "daily_review.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch { toast.error("Export failed"); }
  };

  const printPdf = () => {
    // Browser-native PDF: open a styled printable window
    const w = window.open("", "_blank", "noopener,width=1400,height=900");
    if (!w) return toast.error("Allow pop-ups to print");
    const title = "Daily Review Sheet";
    // Build column list dynamically from tenant's process configuration so
    // the printed PDF matches the on-screen sheet exactly.
    const FIXED_LEFT = [
      ["customer", "Customer"], ["part_no", "Part No"], ["part_name", "Part Name"],
      ["part_rate", "Rate"], ["qty", "Quantity"], ["value", "Value"],
      ["dispatch_qty", "Dispatch Quantity"], ["dispatch_value", "Dispatch Value"],
      ["balance_dispatch", "Balance Dispatch"], ["balance_disp_value", "Balance Dispatch Value"],
    ];
    const PROC_COLS = processCols.flatMap(pc => [
      [`__proc__${pc.code}__qty`, `${pc.name} Quantity`, pc.code, "qty"],
      [`__proc__${pc.code}__val`, `${pc.name} Value`, pc.code, "value"],
    ]);
    const FIXED_RIGHT = [
      ["bal_machining_qty", "Balance Machining Quantity"], ["bal_machining_value", "Balance Machining Value"],
    ];
    const COLS = [...FIXED_LEFT, ...PROC_COLS, ...FIXED_RIGHT];
    const headRow = COLS.map(c => `<th>${c[1]}</th>`).join("");
    const cellFor = (r, c) => {
      if (c.length === 4) {
        // dynamic process cell
        const pb = (r.process_bals || []).find(x => x.code === c[2]) || {};
        const v = pb[c[3]] || 0;
        return `<td class="num">${f2(v)}</td>`;
      }
      const k = c[0];
      return `<td class="${typeof r[k] === 'number' ? 'num' : ''}">${typeof r[k] === 'number' ? f2(r[k]) : (r[k] ?? '')}</td>`;
    };
    const bodyRows = data.rows.map(r => `<tr>${COLS.map(c => cellFor(r, c)).join("")}</tr>`).join("");
    const t = data.totals || {};
    // Totals row mirrors the column order: blank for label cells, sums for numeric.
    const totalsCells = COLS.map((c) => {
      if (c.length === 4) {
        const sum = data.rows.reduce((s, r) => {
          const pb = (r.process_bals || []).find(x => x.code === c[2]);
          return s + (pb?.[c[3]] || 0);
        }, 0);
        return `<td class="num"><b>${f2(sum)}</b></td>`;
      }
      const k = c[0];
      // Map fixed-column key to totals key
      const totalsMap = {
        qty: t.total_qty, value: t.total_value,
        dispatch_value: t.total_dispatch_value, balance_disp_value: t.total_balance_dispatch_value,
        bal_machining_qty: t.total_machining_qty, bal_machining_value: t.total_machining_value,
      };
      if (k in totalsMap) return `<td class="num"><b>${f2(totalsMap[k])}</b></td>`;
      return `<td></td>`;
    }).join("");
    const totalsRow = `<tr class="totals">${totalsCells}</tr>`;

    w.document.write(`
      <html><head><title>${title}</title>
      <style>
        body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;margin:24px;}
        h1{font-size:18px;margin:0 0 12px;color:#1D2951}
        table{width:100%;border-collapse:collapse;font-size:9.5px}
        th{background:#334155;color:#fff;padding:6px;border:1px solid #1f2937;text-align:left}
        td{padding:5px 6px;border:1px solid #CBD5E1}
        tr:nth-child(even) td{background:#F8FAFC}
        td.num{text-align:right;font-family:ui-monospace,monospace}
        tr.totals td{background:#059669!important;color:#fff}
        @media print{ button{display:none} }
      </style></head><body>
      <h1>${title}</h1>
      <button onclick="window.print()" style="margin-bottom:12px;padding:6px 14px;background:#1D2951;color:#fff;border:0;border-radius:4px;cursor:pointer">Print / Save as PDF</button>
      <table><thead><tr>${headRow}</tr></thead><tbody>${bodyRows}${totalsRow}</tbody></table>
      </body></html>
    `);
    w.document.close();
  };

  const totals = data.totals || {};

  return (
    <Layout>
      <div className="p-6 space-y-4" data-testid="daily-review-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: "Chivo" }}>Daily Review Sheet</h1>
            <p className="text-sm text-[#64748B] mt-1">
              Live management value-flow report · auto-refreshes every 30s ·{" "}
              <span className="font-semibold text-[#059669]">{data.count} parts</span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setColPickerOpen(true)} className="text-xs" data-testid="dr-customize-cols-btn">
              <SlidersHorizontal size={14} className="mr-1" /> Customize Columns
            </Button>
            <Button variant="outline" onClick={fetchData} disabled={loading} className="text-xs" data-testid="dr-refresh-btn">
              <ArrowsClockwise size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button onClick={exportExcel} className="bg-[#059669] hover:bg-[#047857] text-white text-xs" data-testid="dr-excel-btn">
              <FileXls size={14} className="mr-1" /> Excel
            </Button>
            <Button onClick={printPdf} className="bg-[#1D2951] hover:bg-[#2A3B73] text-white text-xs" data-testid="dr-pdf-btn">
              <Printer size={14} className="mr-1" /> Print PDF
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 border border-[#E2E8F0]">
          <div className="flex items-center gap-2 mb-3">
            <FunnelSimple size={16} className="text-[#1D2951]" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#1D2951]">Filters</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-[11px] font-semibold">Customer</Label>
              <Select value={filters.customer || "__all__"} onValueChange={v => setFilters(f => ({ ...f, customer: v === "__all__" ? "" : v }))}>
                <SelectTrigger className="min-h-[36px]" data-testid="dr-customer-filter"><SelectValue placeholder="All Customers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Customers</SelectItem>
                  {(data.filters?.customers || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] font-semibold">Part No</Label>
              <Input value={filters.part_no} onChange={e => setFilters(f => ({ ...f, part_no: e.target.value }))} placeholder="Contains…" className="min-h-[36px] font-mono text-sm" data-testid="dr-partno-filter" />
            </div>
            <div>
              <Label className="text-[11px] font-semibold">Date From</Label>
              <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} className="min-h-[36px] text-sm" data-testid="dr-from-filter" />
            </div>
            <div>
              <Label className="text-[11px] font-semibold">Date To</Label>
              <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} className="min-h-[36px] text-sm" data-testid="dr-to-filter" />
            </div>
            <div>
              <Label className="text-[11px] font-semibold">Sort By</Label>
              <Select value={filters.sort} onValueChange={v => setFilters(f => ({ ...f, sort: v }))}>
                <SelectTrigger className="min-h-[36px]" data-testid="dr-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-[#94A3B8]" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customer / part…" className="min-h-[36px] pl-8 text-sm" data-testid="dr-search" />
            </div>
            <Button
              type="button"
              onClick={() => setViewMode(v => v === "rollup" ? "expanded" : "rollup")}
              className={`text-xs h-[36px] px-3 font-bold border ${viewMode === "expanded"
                ? "bg-[#7C3AED] hover:bg-[#5B21B6] text-white border-[#5B21B6]"
                : "bg-white hover:bg-[#F5F3FF] text-[#7C3AED] border-[#DDD6FE]"}`}
              title={viewMode === "expanded"
                ? "Expanded view — every process has its own column. Click to collapse to Schedule / Dispatch / RFD / S/F / Machining."
                : "Default rollup view — click to expand every process into its own column."}
              data-testid="dr-view-mode-toggle"
            >
              {viewMode === "expanded" ? "▾ Expanded" : "▸ Expand"}
            </Button>
            <Button
              type="button"
              onClick={() => setCriticalOnly(v => !v)}
              className={`text-xs h-[36px] px-3 font-bold border ${criticalOnly
                ? "bg-[#DC2626] hover:bg-[#B91C1C] text-white border-[#B91C1C]"
                : "bg-white hover:bg-[#FEF2F2] text-[#DC2626] border-[#FECACA]"}`}
              title={criticalOnly
                ? "Showing only parts flagged as critical in Item Master — click to show all"
                : "Show only parts flagged as critical in Item Master"}
              data-testid="dr-critical-filter"
            >
              {criticalOnly ? "★ Critical Parts (ON)" : "★ Critical Parts"}
            </Button>
            <Button variant="outline" onClick={() => { setFilters({ customer: "", part_no: "", process: "", date_from: "", date_to: "", sort: "balance_value_desc" }); setSearch(""); setCriticalOnly(false); }} className="text-xs" data-testid="dr-reset-btn">
              Reset
            </Button>
          </div>
        </Card>

        {/* Table */}
        <Card className="border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto max-h-[65vh]">
            <table className="w-full text-xs border-collapse" data-testid="daily-review-table" style={{ minWidth: 2000 }}>
              <colgroup>
                <col style={{ width: 38 }} />{/* Critical star */}
                <col style={{ width: 145 }} />{/* Customer */}
                <col style={{ width: 110 }} />{/* Part No. */}
                <col style={{ width: 175 }} />{/* Part Name */}
                <col style={{ width: 75 }} />{/* Part Rate */}
                <col style={{ width: 72 }} />{/* Sched Qty */}
                <col style={{ width: 95 }} />{/* Sched Value */}
                <col style={{ width: 80 }} />{/* Disp Qty */}
                <col style={{ width: 95 }} />{/* Disp Value */}
                <col style={{ width: 82 }} />{/* Balance Dispatch Qty */}
                <col style={{ width: 105 }} />{/* Balance Dispatch Value */}
                {/* Expanded mode: one Qty + Value column pair per tenant process. */}
                {viewMode === "expanded" && processCols.map(pc => (
                  <React.Fragment key={`cg-${pc.code}`}>
                    <col style={{ width: 78 }} />
                    <col style={{ width: 95 }} />
                  </React.Fragment>
                ))}
                {/* Rollup mode: 3 single-value category columns (RFD / S/F / Machining) */}
                {viewMode === "rollup" && (
                  <>
                    <col style={{ width: 90 }} />
                    <col style={{ width: 90 }} />
                    <col style={{ width: 100 }} />
                  </>
                )}
                <col style={{ width: 95 }} />{/* Balance Machining Qty */}
                <col style={{ width: 115 }} />{/* Balance Machining Value */}
              </colgroup>
              <thead className="bg-[#1D2951] text-white sticky top-0 z-10">
                <tr>
                  {[
                    ["★", "center"],
                    ["Customer", "left"], ["Part No.", "left"], ["Part Name", "left"],
                    ["Part Rate", "right"], ["Scheduled Quantity", "right"], ["Scheduled Value", "right"],
                    ["Dispatch Quantity", "right"], ["Dispatch Value", "right"],
                    ["Balance Dispatch Quantity", "right"], ["Balance Dispatch Value", "right"],
                  ].map(([h, align]) => (
                    <th key={h} className={`px-2 py-2.5 text-[11px] font-bold border-r border-[#334155] last:border-r-0 align-middle leading-tight whitespace-normal break-words text-${align}`} style={{ verticalAlign: "middle" }}>
                      {h}
                    </th>
                  ))}
                  {/* Dynamic per-process headers (Qty + Value) — shown only in Expanded mode */}
                  {viewMode === "expanded" && processCols.map(pc => (
                    <React.Fragment key={`th-${pc.code}`}>
                      <th className="px-2 py-2.5 text-[11px] font-bold border-r border-[#334155] align-middle leading-tight whitespace-normal break-words text-right" style={{ verticalAlign: "middle" }}>
                        {pc.name} Quantity
                      </th>
                      <th className="px-2 py-2.5 text-[11px] font-bold border-r border-[#334155] align-middle leading-tight whitespace-normal break-words text-right" style={{ verticalAlign: "middle" }}>
                        {pc.name} Value
                      </th>
                    </React.Fragment>
                  ))}
                  {/* Rollup mode: 3 category tiles (RFD / S/F / Machining) — single WIP value each */}
                  {viewMode === "rollup" && (
                    <>
                      <th className="px-2 py-2.5 text-[11px] font-bold border-r border-[#334155] text-right bg-[#10B981]" style={{ verticalAlign: "middle" }} title="RFD Quantity — WIP at Firewall/RFD-tagged operations (Balance Production + Balance Issue)">
                        RFD
                      </th>
                      <th className="px-2 py-2.5 text-[11px] font-bold border-r border-[#334155] text-right bg-[#8B5CF6]" style={{ verticalAlign: "middle" }} title="S/F Quantity — WIP at Semi-Firewall (Balance Production + Balance Issue)">
                        S/F
                      </th>
                      <th className="px-2 py-2.5 text-[11px] font-bold border-r border-[#334155] text-right bg-[#F59E0B]" style={{ verticalAlign: "middle" }} title="Machining — Balance Dispatch − RFD − S/F">
                        Machining
                      </th>
                    </>
                  )}
                  <th className="px-2 py-2.5 text-[11px] font-bold border-r border-[#334155] text-right" style={{ verticalAlign: "middle" }}>Balance Machining Quantity</th>
                  <th className="px-2 py-2.5 text-[11px] font-bold border-r border-[#334155] last:border-r-0 text-right" style={{ verticalAlign: "middle" }}>Balance Machining Value</th>
                  {/* Custom columns from tenant config */}
                  {customColumns.map(cc => (
                    <th key={cc.code} className="px-2 py-2.5 text-[11px] font-bold border-r border-[#334155] last:border-r-0 align-middle leading-tight whitespace-normal break-words text-right bg-[#3730A3]" title={`Showing ${cc.value_kind} qty`}>
                      {cc.name}
                      <div className="text-[9px] font-normal opacity-70 normal-case">{cc.value_kind}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {pageRows.length === 0 && (
                  <tr><td colSpan={11 + (viewMode === "expanded" ? processCols.length * 2 : 3) + 2 + customColumns.length} className="text-center py-12 text-[#64748B]">{loading ? "Loading…" : "No data for current filters."}</td></tr>
                )}
                {pageRows.map((r, idx) => (
                  <tr key={r.part_id || idx} className={`${r.critical ? 'bg-[#FEF2F2] hover:bg-[#FEE2E2]' : (idx % 2 === 0 ? "bg-white hover:bg-[#EFF6FF]" : "bg-[#F8FAFC] hover:bg-[#EFF6FF]")}`} data-testid={`dr-row-${idx}`}>
                    <td className="px-1 py-1.5 text-center" data-testid={`dr-critical-${idx}`}>
                      {r.critical ? <span className="text-[#DC2626] text-base leading-none" title="Critical part (set in Item Master)">★</span> : null}
                    </td>
                    <td className="px-2 py-1.5 font-semibold text-[#1D2951]">{r.customer || "—"}</td>
                    <td className="px-2 py-1.5 font-mono">
                      <div>{r.part_no}</div>
                      {r.is_pooled && r.company_part_no_description && (
                        <div className="text-[9px] font-normal text-[#64748B] truncate max-w-[180px]" title={r.company_part_no_description}>{r.company_part_no_description}</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-[#475569] truncate max-w-[200px]" title={r.part_name}>{r.part_name}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{f2(r.part_rate)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold">{f2(r.qty)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{f2(r.value)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-[#059669] font-semibold">{f2(r.dispatch_qty)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-[#059669]">{f2(r.dispatch_value)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-[#DC2626] font-semibold">{f2(r.balance_dispatch)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-[#DC2626] font-bold">{f2(r.balance_disp_value)}</td>
                    {/* Dynamic per-process Qty + Value cells — Expanded mode only */}
                    {viewMode === "expanded" && processCols.map(pc => {
                      const pb = (r.process_bals || []).find(x => x.code === pc.code) || { qty: 0, value: 0 };
                      return (
                        <React.Fragment key={`td-${pc.code}-${idx}`}>
                          <td className="px-2 py-1.5 text-right font-mono" data-testid={`dr-${pc.code}-qty-${idx}`}>{f2(pb.qty)}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-[#64748B]" data-testid={`dr-${pc.code}-val-${idx}`}>{f2(pb.value)}</td>
                        </React.Fragment>
                      );
                    })}
                    {/* Rollup mode: 3 single-value category cells (RFD / S/F / Machining) */}
                    {viewMode === "rollup" && (() => {
                      const cr = r.category_rollups || {};
                      const rfd = cr.rfd?.qty || 0;
                      const sf = cr.s_f?.qty || 0;
                      const mach = cr.machined?.qty || 0;
                      const tip = (procs) => (procs || []).map(p => `${p.name}: ${f2(p.qty)}`).join('\n');
                      return (
                        <>
                          <td className="px-2 py-1.5 text-right font-mono font-semibold text-[#065F46] bg-[#ECFDF5]" data-testid={`dr-rfd-${idx}`} title={tip(cr.rfd?.processes)}>{f2(rfd)}</td>
                          <td className="px-2 py-1.5 text-right font-mono font-semibold text-[#5B21B6] bg-[#F5F3FF]" data-testid={`dr-sf-${idx}`} title={tip(cr.s_f?.processes)}>{f2(sf)}</td>
                          <td className="px-2 py-1.5 text-right font-mono font-semibold text-[#92400E] bg-[#FFFBEB]" data-testid={`dr-machined-${idx}`} title="Balance Dispatch − RFD − S/F">{f2(mach)}</td>
                        </>
                      );
                    })()}
                    <td className="px-2 py-1.5 text-right font-mono text-[#7C3AED] font-semibold">{f2(r.bal_machining_qty)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-[#7C3AED] font-bold">{f2(r.bal_machining_value)}</td>
                    {/* Custom columns */}
                    {customColumns.map(cc => {
                      const item = (r.custom_cols || []).find(x => x.code === cc.code);
                      return (
                        <td key={cc.code} className="px-2 py-1.5 text-right font-mono text-[#3730A3] bg-[#EEF2FF]" data-testid={`dr-cc-${cc.code}-${idx}`}>
                          {f2(item ? item.value : 0)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              {data.rows.length > 0 && (
                <tfoot className="sticky bottom-0">
                  <tr className="bg-[#059669] text-white">
                    <td colSpan={5} className="px-2 py-2 text-right font-bold">TOTAL</td>
                    <td className="px-2 py-2 text-right font-mono font-bold">{f2(totals.total_qty)}</td>
                    <td className="px-2 py-2 text-right font-mono font-bold">{f2(totals.total_value)}</td>
                    <td className="px-2 py-2"></td>
                    <td className="px-2 py-2 text-right font-mono font-bold">{f2(totals.total_dispatch_value)}</td>
                    <td className="px-2 py-2"></td>
                    <td className="px-2 py-2 text-right font-mono font-bold">{f2(totals.total_balance_dispatch_value)}</td>
                    {/* Dynamic per-process totals — Expanded mode only */}
                    {viewMode === "expanded" && processCols.map(pc => {
                      const qtySum = data.rows.reduce((s, r) => {
                        const pb = (r.process_bals || []).find(x => x.code === pc.code);
                        return s + (pb?.qty || 0);
                      }, 0);
                      const valSum = data.rows.reduce((s, r) => {
                        const pb = (r.process_bals || []).find(x => x.code === pc.code);
                        return s + (pb?.value || 0);
                      }, 0);
                      return (
                        <React.Fragment key={`tot-${pc.code}`}>
                          <td className="px-2 py-2 text-right font-mono font-bold">{f2(qtySum)}</td>
                          <td className="px-2 py-2 text-right font-mono font-bold">{f2(valSum)}</td>
                        </React.Fragment>
                      );
                    })}
                    {/* Rollup category totals — single WIP value per bucket */}
                    {viewMode === "rollup" && (() => {
                      const sumCat = (bucket) => data.rows.reduce((s, r) => s + ((r.category_rollups || {})[bucket]?.qty || 0), 0);
                      return (
                        <>
                          <td className="px-2 py-2 text-right font-mono font-bold">{f2(sumCat('rfd'))}</td>
                          <td className="px-2 py-2 text-right font-mono font-bold">{f2(sumCat('s_f'))}</td>
                          <td className="px-2 py-2 text-right font-mono font-bold">{f2(sumCat('machined'))}</td>
                        </>
                      );
                    })()}
                    <td className="px-2 py-2 text-right font-mono font-bold">{f2(totals.total_machining_qty)}</td>
                    <td className="px-2 py-2 text-right font-mono font-bold">{f2(totals.total_machining_value)}</td>
                    {/* Custom column totals */}
                    {customColumns.map(cc => {
                      const sum = data.rows.reduce((s, r) => {
                        const item = (r.custom_cols || []).find(x => x.code === cc.code);
                        return s + (item ? item.value : 0);
                      }, 0);
                      return (
                        <td key={cc.code} className="px-2 py-2 text-right font-mono font-bold bg-[#10B981]">{f2(sum)}</td>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2 bg-[#F8FAFC] border-t border-[#E2E8F0] text-xs">
              <span className="text-[#64748B]">Page {page} / {totalPages} · Showing {pageRows.length} of {data.rows.length}</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-7 px-3 text-xs" data-testid="dr-prev-page">Prev</Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-7 px-3 text-xs" data-testid="dr-next-page">Next</Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Customize Columns Dialog */}
      <Dialog open={colPickerOpen} onOpenChange={setColPickerOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dr-col-picker">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#1D2951]">Customize Columns — Daily Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* ── Section 1: Auto WIP columns — show/hide per process ── */}
            <div>
              <p className="text-xs font-bold text-[#1D2951] mb-1">PROCESS WIP COLUMNS</p>
              <p className="text-xs text-[#64748B] mb-2">
                These columns appear automatically for every process you have in Customization (one Quantity + Value pair each). Uncheck any process you don't want to see on the sheet.
              </p>
              <div className="border border-[#E2E8F0] rounded">
                <table className="w-full text-xs">
                  <thead className="bg-[#F1F5F9]">
                    <tr>
                      <th className="px-2 py-2 text-left w-12">Show</th>
                      <th className="px-2 py-2 text-left">Process</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {processList.filter(p => p.code !== "dispatch").map(p => {
                      const hidden = (colDraft.hidden_processes || []).includes(p.code);
                      return (
                        <tr key={`hide-${p.code}`} className={hidden ? "bg-[#FEF2F2]" : "bg-[#F0FDF4]"} data-testid={`hide-row-${p.code}`}>
                          <td className="px-2 py-1.5">
                            <Checkbox checked={!hidden} onCheckedChange={() => toggleHideProcess(p.code)} data-testid={`hide-cb-${p.code}`} />
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="font-semibold">{p.name}</div>
                            <div className="text-[10px] text-[#94A3B8] font-mono">{p.code}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Section 2: Extra value-kind columns (Produced/Issued/OK/Balance) ── */}
            <div className="pt-3 border-t border-[#E2E8F0]">
              <p className="text-xs font-bold text-[#1D2951] mb-1">EXTRA VALUE COLUMNS (OPTIONAL)</p>
              <p className="text-xs text-[#64748B] mb-2">
                Add additional per-process columns showing a specific value type (Produced, Issued, OK, or Balance) instead of WIP.
              </p>
              <div className="border border-[#E2E8F0] rounded">
                <table className="w-full text-xs">
                  <thead className="bg-[#F1F5F9]">
                    <tr>
                      <th className="px-2 py-2 text-left w-12">Show</th>
                      <th className="px-2 py-2 text-left">Process</th>
                      <th className="px-2 py-2 text-left w-44">Value Kind</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {processList.map(p => {
                      const checked = colDraft.codes.includes(p.code);
                      const kind = colDraft.value_kind_by_code[p.code] || "produced";
                      return (
                        <tr key={p.code} className={checked ? "bg-[#EFF6FF]" : ""} data-testid={`col-row-${p.code}`}>
                          <td className="px-2 py-1.5">
                            <Checkbox checked={checked} onCheckedChange={() => toggleCol(p.code)} data-testid={`col-cb-${p.code}`} />
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="font-semibold">{p.name}</div>
                            <div className="text-[10px] text-[#94A3B8] font-mono">{p.code}</div>
                          </td>
                          <td className="px-2 py-1.5">
                            <select value={kind} onChange={e => setColKind(p.code, e.target.value)}
                              disabled={!checked}
                              className="w-full min-h-[30px] border border-[#E2E8F0] rounded px-2 text-xs disabled:bg-[#F8FAFC] disabled:text-[#94A3B8]"
                              data-testid={`col-kind-${p.code}`}>
                              <option value="produced">Produced / Inspected</option>
                              <option value="issued">Issued</option>
                              <option value="ok">OK</option>
                              <option value="balance">Balance (Production − Issue)</option>
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setColPickerOpen(false)}><X size={14} className="mr-1" /> Cancel</Button>
              <Button onClick={saveColumnConfig} className="bg-[#1D2951] hover:bg-[#2A3B73] text-white" data-testid="col-save-btn">
                <Check size={14} className="mr-1" /> Save & Apply
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default DailyReviewSheet;
